import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { API_BASE as _apiBase } from '../api';

const defaultSettings = {
  luminosita: 0,
  contrasto: 0,
  saturazione: 0,
  zoom: 100,
  rotazione: 0,
  specchia: false,
  cornice: 'nessuna',
  spessoreCornice: 6,
  coloreCornice: '#ffffff',
  watermark: false,
  watermarkTesto: '@ImperoElite',
  watermarkPos: 'bottomRight'
};

// ─── Disegna cornice sul canvas ──────────────────────────
function disegnaCornice(ctx, w, h, stile, spessore, colore) {
  if (!stile || stile === 'nessuna') return;
  ctx.strokeStyle = colore || '#ffffff';
  ctx.lineWidth = spessore || 6;
  const m = (spessore || 6) / 2;

  if (stile === 'sottile' || stile === 'spessa') {
    ctx.strokeRect(m, m, w - (spessore || 6), h - (spessore || 6));
  } else if (stile === 'doppia') {
    ctx.strokeRect(m, m, w - (spessore || 6), h - (spessore || 6));
    ctx.lineWidth = Math.max(1, (spessore || 6) * 0.4);
    const gap = (spessore || 6) * 2;
    ctx.strokeRect(gap, gap, w - gap * 2, h - gap * 2);
  } else if (stile === 'arrotondata') {
    const r = Math.min(w, h) * 0.06;
    ctx.beginPath();
    ctx.moveTo(m + r, m);
    ctx.lineTo(w - m - r, m); ctx.arcTo(w - m, m, w - m, m + r, r);
    ctx.lineTo(w - m, h - m - r); ctx.arcTo(w - m, h - m, w - m - r, h - m, r);
    ctx.lineTo(m + r, h - m); ctx.arcTo(m, h - m, m, h - m - r, r);
    ctx.lineTo(m, m + r); ctx.arcTo(m, m, m + r, m, r);
    ctx.closePath(); ctx.stroke();
  } else if (stile === 'ombra') {
    ctx.shadowColor = colore || '#ffffff';
    ctx.shadowBlur = (spessore || 6) * 4;
    ctx.shadowOffsetX = (spessore || 6) * 0.5;
    ctx.shadowOffsetY = (spessore || 6) * 0.5;
    ctx.strokeRect(m, m, w - (spessore || 6), h - (spessore || 6));
    ctx.shadowBlur = 0; ctx.shadowOffsetX = 0; ctx.shadowOffsetY = 0;
  }
}

// ─── Disegna watermark sul canvas ────────────────────────
function disegnaWatermark(ctx, size, s) {
  if (!s.watermark || !s.watermarkTesto) return;
  ctx.font = `bold ${Math.floor(size * 0.06)}px DM Sans, sans-serif`;
  ctx.fillStyle = 'rgba(255,255,255,0.5)';
  ctx.shadowColor = 'rgba(0,0,0,0.8)';
  ctx.shadowBlur = 4;
  const tw = ctx.measureText(s.watermarkTesto).width;
  const pad = 15;
  let x = pad, y = size - pad;
  const pos = s.watermarkPos || 'bottomRight';
  if (pos === 'bottomRight') { x = size - tw - pad; y = size - pad; }
  else if (pos === 'bottomLeft') { x = pad; y = size - pad; }
  else if (pos === 'topRight') { x = size - tw - pad; y = pad + 20; }
  else if (pos === 'topLeft') { x = pad; y = pad + 20; }
  else if (pos === 'center') { x = (size - tw) / 2; y = size / 2; }
  ctx.fillText(s.watermarkTesto, x, y);
  ctx.shadowBlur = 0;
}

// ─── Disegna immagine con effetti sul canvas ─────────────
function disegnaImmagine(ctx, img, size, s) {
  ctx.clearRect(0, 0, size, size);
  ctx.filter = `brightness(${100 + s.luminosita}%) contrast(${100 + s.contrasto}%) saturate(${100 + s.saturazione}%)`;
  ctx.save();
  ctx.translate(size / 2, size / 2);
  ctx.rotate((s.rotazione || 0) * Math.PI / 180);
  if (s.specchia) ctx.scale(-1, 1);
  const scale = (s.zoom || 100) / 100;
  const imgAspect = img.width / img.height;
  let dw, dh;
  if (imgAspect > 1) { dh = size * scale; dw = dh * imgAspect; }
  else { dw = size * scale; dh = dw / imgAspect; }
  ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
  ctx.restore();
  ctx.filter = 'none';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
  disegnaCornice(ctx, size, size, s.cornice, s.spessoreCornice, s.coloreCornice);
  disegnaWatermark(ctx, size, s);
  // Reset finale
  ctx.filter = 'none';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
}

const PhotoEditor = forwardRef(function PhotoEditor({ fotoUrls, onFotoModificate, apiBase = _apiBase, defaultAperto = false }, ref) {
  const [aperto, setAperto] = useState(defaultAperto);
  const [selezionata, setSelezionata] = useState(0);
  const [settingsMap, setSettingsMap] = useState({});
  const [fotoEditate, setFotoEditate] = useState({});
  const [currentImg, setCurrentImg] = useState(null);
  const [applicatoATutte, setApplicatoATutte] = useState(false);
  const [applicandoATutte, setApplicandoATutte] = useState(false);
  const canvasRef = useRef(null);
  const settingsRef = useRef(null);

  const getSettings = useCallback((idx) => {
    return settingsMap[idx] || { ...defaultSettings };
  }, [settingsMap]);

  const settings = getSettings(selezionata);
  settingsRef.current = settings;

  const updateSetting = useCallback((key, value) => {
    setSettingsMap(prev => ({
      ...prev,
      [selezionata]: { ...(prev[selezionata] || { ...defaultSettings }), [key]: value }
    }));
  }, [selezionata]);

  const resolveUrl = useCallback((url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
    return apiBase + (url.startsWith('/') ? '' : '/') + url;
  }, [apiBase]);

  // Costruisci URL proxy per bypassare CORS su canvas
  const proxyUrl = useCallback((url) => {
    if (!url) return '';
    if (url.startsWith('data:') || url.startsWith('blob:')) return url;
    let path = url;
    if (url.startsWith('http')) {
      try { path = '/' + new URL(url).pathname.replace(/^\//, ''); } catch { path = url; }
    } else if (!url.startsWith('/')) {
      path = '/media/' + url;
    }
    return `${apiBase}/proxy-image?url=${encodeURIComponent(path)}`;
  }, [apiBase]);

  useEffect(() => {
    if (!fotoUrls || fotoUrls.length === 0 || !aperto) return;
    const rawUrl = fotoUrls[selezionata];
    if (!rawUrl) return;

    let revoke = null;

    if (rawUrl.startsWith('data:') || rawUrl.startsWith('blob:')) {
      const img = new Image();
      img.onload = () => setCurrentImg(img);
      img.onerror = () => setCurrentImg(null);
      img.src = rawUrl;
    } else {
      const fetchUrl = proxyUrl(rawUrl);
      fetch(fetchUrl)
        .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob(); })
        .then(blob => {
          const objectUrl = URL.createObjectURL(blob);
          revoke = objectUrl;
          const img = new Image();
          img.onload = () => {
            console.log('[Canvas] Immagine caricata via proxy:', img.naturalWidth, 'x', img.naturalHeight);
            setCurrentImg(img);
          };
          img.onerror = () => { URL.revokeObjectURL(objectUrl); setCurrentImg(null); };
          img.src = objectUrl;
        })
        .catch((e) => {
          console.error('[Canvas] Proxy fallito:', e.message);
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => setCurrentImg(img);
          img.onerror = () => setCurrentImg(null);
          img.src = resolveUrl(rawUrl);
        });
    }

    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [selezionata, fotoUrls, aperto, proxyUrl, resolveUrl]);

  const renderCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !currentImg) return;
    const ctx = canvas.getContext('2d');
    const size = 300;
    canvas.width = size;
    canvas.height = size;
    // Assicurati che il canvas non sia nero: pulisci prima
    ctx.clearRect(0, 0, size, size);
    if (currentImg.naturalWidth === 0 || currentImg.naturalHeight === 0) {
      console.warn('[Canvas] Immagine con dimensioni 0, skip');
      return;
    }
    disegnaImmagine(ctx, currentImg, size, settings);
    // Reset finale filter/shadow
    ctx.filter = 'none';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
  }, [currentImg, settings]);

  useEffect(() => { renderCanvas(); }, [renderCanvas]);

  const exportCanvas = useCallback((img, s) => {
    const offCanvas = document.createElement('canvas');
    const size = 300;
    offCanvas.width = size;
    offCanvas.height = size;
    disegnaImmagine(offCanvas.getContext('2d'), img, size, s);
    return offCanvas.toDataURL('image/jpeg', 0.92);
  }, []);

  const applicaAQuesta = useCallback(() => {
    if (!currentImg) return;
    const base64 = exportCanvas(currentImg, settings);
    setFotoEditate(prev => {
      const next = { ...prev, [selezionata]: base64 };
      const result = (fotoUrls || []).map((_, i) => next[i] || null);
      if (onFotoModificate) onFotoModificate(result);
      return next;
    });
  }, [currentImg, settings, selezionata, exportCanvas, fotoUrls, onFotoModificate]);

  // Metodo principale: fetch via proxy → blob → objectURL (mai tainted)
  const processaFoto = useCallback(async (url, s, idx) => {
    if (!url) return null;
    const fetchUrl = proxyUrl(url);
    console.log(`[Foto ${idx + 1}] Proxy fetch:`, fetchUrl);
    console.log(`[Foto ${idx + 1}] Settings:`, JSON.stringify(s));
    try {
      const response = await fetch(fetchUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      return await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          try {
            const c = document.createElement('canvas');
            const size = Math.max(img.naturalWidth || 800, img.naturalHeight || 800);
            c.width = size; c.height = size;
            disegnaImmagine(c.getContext('2d'), img, size, s);
            const b64 = c.toDataURL('image/jpeg', 0.85);
            console.log(`[Foto ${idx + 1}] ✅ OK (${Math.round(b64.length / 1024)}KB)`);
            URL.revokeObjectURL(objectUrl);
            resolve(b64);
          } catch (e) { URL.revokeObjectURL(objectUrl); console.error(`[Foto ${idx + 1}] Canvas error:`, e); resolve(null); }
        };
        img.onerror = () => { URL.revokeObjectURL(objectUrl); resolve(null); };
        img.src = objectUrl;
      });
    } catch (e) {
      console.error(`[Foto ${idx + 1}] Proxy fetch fallito:`, e.message);
      return null;
    }
  }, [proxyUrl]);

  // Helper: applica settings a tutte le foto
  const applicaSettingsATutte = useCallback(async (s) => {
    setApplicandoATutte(true);
    const output = [];
    for (let i = 0; i < fotoUrls.length; i++) {
      console.log(`[ApplicaTutte] Processo foto ${i + 1}/${fotoUrls.length}`);
      const b64 = await processaFoto(fotoUrls[i], s, i);
      output.push(b64);
      await new Promise(r => setTimeout(r, 150));
    }
    // Fallback: se null usa originale
    const finale = output.map((f, i) => f || null);
    const successi = output.filter(Boolean).length;
    console.log(`[ApplicaTutte] Completato: ${successi}/${fotoUrls.length} foto`);
    const nextEditate = {};
    const nextSettings = {};
    finale.forEach((f, i) => { if (f) { nextEditate[i] = f; nextSettings[i] = { ...s }; } });
    setFotoEditate(prev => ({ ...prev, ...nextEditate }));
    setSettingsMap(prev => ({ ...prev, ...nextSettings }));
    if (onFotoModificate) onFotoModificate(finale);
    setApplicandoATutte(false);
    setApplicatoATutte(true);
    setTimeout(() => setApplicatoATutte(false), 3000);
  }, [fotoUrls, processaFoto, onFotoModificate]);

  const applicaATutte = useCallback(async () => {
    if (!fotoUrls || fotoUrls.length === 0) return;
    const currentSettings = settingsRef.current || settings;
    console.log('[ApplicaTutte] Settings da applicare:', JSON.stringify(currentSettings));
    console.log(`[ApplicaTutte] fotoUrls totali: ${fotoUrls.length}`, fotoUrls);
    await applicaSettingsATutte(currentSettings);
  }, [fotoUrls, settings, applicaSettingsATutte]);

  // Esponi metodi al parent via ref
  useImperativeHandle(ref, () => ({
    applicaTutte: applicaATutte,
    applicaAQuesta: applicaAQuesta,
    haModifiche: Object.keys(settingsMap).length > 0,
    fotoSalvate: Object.keys(fotoEditate).length,
  }), [applicaATutte, applicaAQuesta, settingsMap, fotoEditate]);

  const handleRandom = useCallback(async () => {
    if (!fotoUrls || fotoUrls.length === 0) return;
    const randomSettings = {
      luminosita: Math.round((Math.random() * 20) - 10),
      contrasto: Math.round((Math.random() * 16) - 8),
      saturazione: Math.round((Math.random() * 10) - 5),
      zoom: Math.round(100 + (Math.random() * 5)),
      rotazione: 0,
      specchia: false,
      cornice: Math.random() > 0.7 ? 'sottile' : 'nessuna',
      spessoreCornice: Math.floor(Math.random() * 2) + 1,
      coloreCornice: Math.random() > 0.5 ? '#ffffff' : '#f0f0f0',
      watermark: settings.watermark,
      watermarkTesto: settings.watermarkTesto,
      watermarkPos: settings.watermarkPos,
    };
    console.log('[Random] Settings:', randomSettings);
    // Aggiorna settings visivamente per la foto selezionata
    setSettingsMap(prev => ({ ...prev, [selezionata]: { ...randomSettings } }));
    await new Promise(r => setTimeout(r, 300));
    await applicaSettingsATutte(randomSettings);
  }, [fotoUrls, settings, selezionata, applicaSettingsATutte]);

  const resetFoto = useCallback(() => {
    setSettingsMap(prev => { const next = { ...prev }; delete next[selezionata]; return next; });
    setFotoEditate(prev => {
      const next = { ...prev }; delete next[selezionata];
      const result = (fotoUrls || []).map((_, i) => next[i] || null);
      if (onFotoModificate) onFotoModificate(result);
      return next;
    });
  }, [selezionata, fotoUrls, onFotoModificate]);

  if (!fotoUrls || fotoUrls.length === 0) return null;

  const sliders = [
    { key: 'luminosita', label: '☀️ Luminosità', min: -100, max: 100 },
    { key: 'contrasto', label: '◐ Contrasto', min: -100, max: 100 },
    { key: 'saturazione', label: '🎨 Saturazione', min: -100, max: 100 },
    { key: 'zoom', label: '🔍 Zoom', min: 100, max: 150 }
  ];

  const corniciPreset = [
    { id: 'nessuna', label: '✕' },
    { id: 'sottile' },
    { id: 'spessa' },
    { id: 'doppia' },
    { id: 'arrotondata' },
    { id: 'ombra' },
  ];

  const btnStyle = (active) => ({
    flex: 1, background: active ? 'rgba(124,58,237,0.2)' : '#1c1640',
    border: `1px solid ${active ? '#7c3aed' : '#2d1b69'}`, borderRadius: 20,
    padding: '6px', fontSize: '0.78rem', color: active ? '#a78bfa' : '#e2e8f0',
    cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
  });

  return (
    <div style={{ marginTop: 12 }}>
      {/* Header collassabile */}
      <div onClick={() => setAperto(!aperto)} style={{
        display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', padding: '8px 0'
      }}>
        <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.85rem', fontWeight: 600, color: '#a78bfa' }}>
          📷 Modifica foto prima di pubblicare
        </span>
        <span style={{ display: 'inline-block', transition: 'transform 0.2s', transform: aperto ? 'rotate(180deg)' : 'rotate(0deg)', color: '#a78bfa', fontSize: '0.8rem' }}>▼</span>
      </div>

      {aperto && (
        <div style={{ background: '#151230', borderRadius: 12, padding: 16, marginTop: 4, border: '1px solid #2d1b69' }}>
          {/* Griglia foto */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, maxHeight: 160, overflowY: 'auto', marginBottom: 12 }}>
            {fotoUrls.map((url, idx) => (
              <div key={idx} style={{ position: 'relative', cursor: 'pointer' }} onClick={() => setSelezionata(idx)}>
                <img src={resolveUrl(url)} alt={`Foto ${idx + 1}`} style={{
                  width: 80, height: 80, objectFit: 'cover', borderRadius: 10,
                  border: selezionata === idx ? '2px solid #a78bfa' : '2px solid transparent',
                  boxShadow: selezionata === idx ? '0 0 12px rgba(124,58,237,0.3)' : 'none'
                }} />
                {(fotoEditate[idx] || applicatoATutte) && (
                  <span style={{ position: 'absolute', top: 4, right: 4, background: '#22c55e', color: 'white', fontSize: '0.6rem', fontFamily: 'DM Sans, sans-serif', padding: '1px 5px', borderRadius: 6, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 2 }}>
                    {applicatoATutte ? '✓' : 'Modificata'}
                  </span>
                )}
              </div>
            ))}
          </div>

          {/* Canvas editor */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
            <canvas ref={canvasRef} width={300} height={300} style={{ width: 300, height: 300, borderRadius: 12, border: '1px solid #2d1b69', background: '#0f0d1a' }} />
          </div>

          {/* Slider luminosità/contrasto/saturazione/zoom */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12 }}>
            {sliders.map(({ key, label, min, max }) => (
              <div key={key}>
                <label style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.72rem', color: '#6b7280', display: 'block', marginBottom: 4 }}>
                  {label}: {settings[key]}
                </label>
                <input type="range" min={min} max={max} value={settings[key]}
                  onChange={(e) => updateSetting(key, Number(e.target.value))}
                  style={{ width: '100%', appearance: 'none', WebkitAppearance: 'none', height: 4, background: '#2d1b69', borderRadius: 2, outline: 'none', accentColor: '#7c3aed' }}
                />
              </div>
            ))}
          </div>

          {/* Slider rotazione libera */}
          <div style={{ marginTop: 12 }}>
            <label style={{ fontSize: '0.75rem', color: '#a78bfa', fontFamily: 'DM Sans, sans-serif', display: 'block', marginBottom: 6 }}>
              ↻ Rotazione: {settings.rotazione || 0}°
            </label>
            <input type="range" min="-180" max="180" step="1"
              value={settings.rotazione || 0}
              onChange={e => updateSetting('rotazione', parseInt(e.target.value))}
              style={{ width: '100%', accentColor: '#7c3aed', appearance: 'none', WebkitAppearance: 'none', height: 4, background: '#2d1b69', borderRadius: 2, outline: 'none' }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button onClick={() => updateSetting('rotazione', (settings.rotazione || 0) - 90)} style={btnStyle(false)}>↺ -90°</button>
              <button onClick={() => updateSetting('rotazione', (settings.rotazione || 0) + 90)} style={btnStyle(false)}>↻ +90°</button>
              <button onClick={() => updateSetting('specchia', !settings.specchia)} style={btnStyle(settings.specchia)}>⟷ Specchia</button>
              <button onClick={() => updateSetting('rotazione', 0)} style={{ ...btnStyle(false), color: '#6b7280' }}>↺ Reset</button>
            </div>
          </div>

          {/* 6 preset cornici */}
          <div style={{ marginTop: 14 }}>
            <label style={{ fontSize: '0.75rem', color: '#6b7280', fontFamily: 'DM Sans, sans-serif', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Stile cornice
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {corniciPreset.map(stile => (
                <div key={stile.id} onClick={() => updateSetting('cornice', stile.id)} style={{
                  width: 52, height: 52, borderRadius: 8, cursor: 'pointer',
                  background: '#1c1640', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: settings.cornice === stile.id ? '2px solid #a78bfa' : '2px solid #2d1b69',
                  boxShadow: settings.cornice === stile.id ? '0 0 8px rgba(167,139,250,0.4)' : 'none',
                  position: 'relative', overflow: 'hidden', transition: '0.15s'
                }}>
                  {stile.id === 'nessuna' && <span style={{ color: '#6b7280', fontSize: '1.2rem' }}>✕</span>}
                  {stile.id === 'sottile' && <div style={{ position: 'absolute', inset: 4, border: '2px solid white', borderRadius: 2 }} />}
                  {stile.id === 'spessa' && <div style={{ position: 'absolute', inset: 4, border: '6px solid white', borderRadius: 2 }} />}
                  {stile.id === 'doppia' && <>
                    <div style={{ position: 'absolute', inset: 3, border: '2px solid white', borderRadius: 2 }} />
                    <div style={{ position: 'absolute', inset: 8, border: '1px solid white', borderRadius: 2 }} />
                  </>}
                  {stile.id === 'arrotondata' && <div style={{ position: 'absolute', inset: 4, border: '3px solid white', borderRadius: 10 }} />}
                  {stile.id === 'ombra' && <div style={{ position: 'absolute', inset: 6, background: 'transparent', boxShadow: '0 0 8px 3px rgba(255,255,255,0.6)', borderRadius: 2 }} />}
                </div>
              ))}
            </div>

            {/* Controlli spessore e colore */}
            {settings.cornice && settings.cornice !== 'nessuna' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
                <div>
                  <label style={{ fontSize: '0.72rem', color: '#6b7280', fontFamily: 'DM Sans, sans-serif', display: 'block', marginBottom: 4 }}>
                    Spessore: {settings.spessoreCornice || 6}px
                  </label>
                  <input type="range" min="1" max="40" step="1"
                    value={settings.spessoreCornice || 6}
                    onChange={e => updateSetting('spessoreCornice', parseInt(e.target.value))}
                    style={{ width: '100%', accentColor: '#7c3aed', appearance: 'none', WebkitAppearance: 'none', height: 4, background: '#2d1b69', borderRadius: 2, outline: 'none' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.72rem', color: '#6b7280', fontFamily: 'DM Sans, sans-serif', display: 'block', marginBottom: 4 }}>
                    Colore cornice
                  </label>
                  <input type="color"
                    value={settings.coloreCornice || '#ffffff'}
                    onChange={e => updateSetting('coloreCornice', e.target.value)}
                    style={{ width: 48, height: 36, borderRadius: 8, border: '1px solid #2d1b69', cursor: 'pointer', padding: 2, background: '#1c1640' }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Watermark */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
            <div onClick={() => updateSetting('watermark', !settings.watermark)} style={{
              width: 36, height: 20, borderRadius: 10, background: settings.watermark ? '#7c3aed' : '#2d1b69',
              cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0
            }}>
              <div style={{ width: 16, height: 16, borderRadius: '50%', background: 'white', position: 'absolute', top: 2, left: settings.watermark ? 18 : 2, transition: 'left 0.2s' }} />
            </div>
            <input type="text" value={settings.watermarkTesto} onChange={(e) => updateSetting('watermarkTesto', e.target.value)}
              style={{ background: '#1c1640', border: '1px solid #2d1b69', borderRadius: 8, padding: '6px 10px', fontSize: '0.78rem', color: '#e2e8f0', fontFamily: 'DM Sans, sans-serif', outline: 'none', flex: 1, minWidth: 100 }}
            />
            <select value={settings.watermarkPos} onChange={(e) => updateSetting('watermarkPos', e.target.value)}
              style={{ background: '#1c1640', border: '1px solid #2d1b69', borderRadius: 8, padding: '6px 10px', fontSize: '0.78rem', color: '#e2e8f0', fontFamily: 'DM Sans, sans-serif', outline: 'none', cursor: 'pointer' }}>
              <option value="bottomRight">In basso a destra</option>
              <option value="bottomLeft">In basso a sinistra</option>
              <option value="topRight">In alto a destra</option>
              <option value="topLeft">In alto a sinistra</option>
              <option value="center">Centro</option>
            </select>
          </div>

          {/* Pulsanti azione */}
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <button onClick={resetFoto} style={{ background: 'transparent', border: '1px solid #2d1b69', borderRadius: 20, padding: '6px 14px', fontSize: '0.78rem', color: '#a78bfa', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
              ↩ Reset foto
            </button>
            <button onClick={applicaAQuesta} style={{ background: '#7c3aed', border: 'none', borderRadius: 20, padding: '6px 14px', fontSize: '0.78rem', color: 'white', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
              ✅ Applica a questa foto
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={applicaATutte} disabled={applicandoATutte} style={{ background: applicandoATutte ? '#4c3a8e' : '#7c3aed', border: 'none', borderRadius: 20, padding: '6px 14px', fontSize: '0.78rem', color: 'white', cursor: applicandoATutte ? 'wait' : 'pointer', fontFamily: 'DM Sans, sans-serif', opacity: applicandoATutte ? 0.7 : 1 }}>
                {applicandoATutte ? '⏳ Applicando...' : '✅ Applica a tutte'}
              </button>
              <button onClick={handleRandom} disabled={applicandoATutte} style={{
                background: 'linear-gradient(135deg,rgba(124,58,237,0.15),rgba(79,70,229,0.1))',
                border: '1px solid #7c3aed', borderRadius: 20, padding: '6px 14px', fontSize: '0.78rem',
                color: '#a78bfa', cursor: applicandoATutte ? 'wait' : 'pointer', fontFamily: 'DM Sans, sans-serif',
                display: 'flex', alignItems: 'center', gap: 6, opacity: applicandoATutte ? 0.7 : 1
              }}>
                🎲 Random
              </button>
              <button onClick={() => {
                setFotoEditate({});
                setSettingsMap({});
                if (onFotoModificate) onFotoModificate((fotoUrls || []).map(() => null));
                setApplicatoATutte(false);
                console.log('[Reset] Tutte le foto ripristinate agli originali');
              }} style={{
                background: 'rgba(248,113,113,0.1)', border: '1px solid #f87171', borderRadius: 20,
                padding: '6px 14px', fontSize: '0.78rem', color: '#f87171', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif'
              }}>
                ↩ Reset tutte
              </button>
              {applicatoATutte && (
                <span style={{ background: 'rgba(74,222,128,0.15)', border: '1px solid #4ade80', borderRadius: 20, padding: '4px 10px', fontSize: '0.72rem', fontWeight: 700, color: '#4ade80', fontFamily: 'DM Sans, sans-serif' }}>
                  ✓ Applicate a {fotoUrls.length} foto
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none; appearance: none;
          width: 14px; height: 14px; border-radius: 50%;
          background: #7c3aed; cursor: pointer; border: none;
        }
        input[type="range"]::-moz-range-thumb {
          width: 14px; height: 14px; border-radius: 50%;
          background: #7c3aed; cursor: pointer; border: none;
        }
      `}</style>
    </div>
  );
});

export default PhotoEditor;
